import { createSelector } from 'reselect';
import { ActionReducer, combineReducers } from '@ngrx/store';
import { RouterState, routerReducer } from '@ngrx/router-store';
import { Observable } from 'rxjs/Observable';
import '@ngrx/core/add/operator/select';
import { compose } from '@ngrx/core/compose';

import { storeLogger } from 'ngrx-store-logger';

import { environment } from '../../environments/environment';
import { storeFreeze } from 'ngrx-store-freeze';

import { IApplicationState } from './states/applicationState';
import { IContainersState, IApplicationContainerState, IApplicationNotificationState, IPortalInformationState, IAppVersionState } from './states/containersState';
import { IDataState, IAuthenticationDataState, IStageDataState } from './states/dataState';

import * as fromApplicationReducer from './reducers/containers/application.reducer';
import * as fromNotificationReducer from './reducers/containers/notification.reducer';
import * as fromPortalInfoReducer from './reducers/containers/portalInfo.reducer';
import * as fromAppVersionReducer from './reducers/containers/appVersion.reducer';
import * as fromAuthenticationReducer from './reducers/data/authentication.reducer';
import * as fromStageReducer from './reducers/data/stage.reducer';
import * as metaReducers from './reducers/metaReducer';

import * as authenticationActions from './actions/data/authentication';
import { getAttachmentEntities } from './reducers/data/stage.reducer';

const containersReducers = combineReducers({
  application: fromApplicationReducer.applicationReducer,
  notification: fromNotificationReducer.notificationReducer,
  portalInfo: fromPortalInfoReducer.portalInfoReducer,
  applicationVersion: fromAppVersionReducer.applicationVersionReducer
});

const dataReducers = combineReducers({
  authentication: metaReducers.analyticsAuthenticationMetaReducer(fromAuthenticationReducer.authenticationReducer),
  stage: metaReducers.analyticsStageMetaReducer(fromStageReducer.stageReducer)
});

//const loggerMetaReducer = storeLogger();


const reducers = {
  router: metaReducers.analyticsRouterMetaReducer(routerReducer),
  containers: containersReducers,
  data: dataReducers
};

const developmentReducer: ActionReducer<IApplicationState> = compose(storeFreeze, combineReducers)(reducers);
const productionReducer: ActionReducer<IApplicationState> = combineReducers(reducers);

export function rootReducer(state: any, action: any) {
    // Reset all states
    // if (action.type === authenticationActions.ActionTypes.SERVER_LOGOUT) {
    //   console.log('[Debug] Logout - Reset State');
    //   state = undefined;
    // }

    if (environment.production) {
        return productionReducer(state, action);
    } else {
        return developmentReducer(state, action);
    }
}

// Containers State selectors
export const getContainersState = (applicationState: IApplicationState): IContainersState => applicationState.containers;
export const getApplicationContainerState = createSelector(
  getContainersState,
  (containersState: IContainersState): IApplicationContainerState => containersState.application
);

export const isApplicationBusy = createSelector(getApplicationContainerState, fromApplicationReducer.getIsBusy);
export const getApplicationProgress = createSelector(getApplicationContainerState, fromApplicationReducer.getProgress);
// notifications
export const getApplicationNotificationState = createSelector(
  getContainersState,
  (containersState: IContainersState): IApplicationNotificationState => containersState.notification
);
export const getApplicationNotifications = createSelector(getApplicationNotificationState, fromNotificationReducer.getNotifications);
export const getApplicationLatestNotification = createSelector(getApplicationNotificationState, fromNotificationReducer.getLatestNotification);

// Portal Information
export const getApplicationPortalInformationState = createSelector(
  getContainersState,
  (containersState: IContainersState): IPortalInformationState => containersState.portalInfo
);
export const getApplicationPortalInformation = createSelector(getApplicationPortalInformationState, fromPortalInfoReducer.getPortalInformation);

// Application version
export const getApplicationVersionState = createSelector (
  getContainersState,
  (containerState: IContainersState): IAppVersionState => containerState.applicationVersion
);

export const getApplicationVersion = createSelector(getApplicationVersionState, fromAppVersionReducer.getApplicationVersion);

// Data State selectors
export const getDataState = (applicationState: IApplicationState): IDataState => applicationState.data;

// Data: Authentication State selectors
export const getAuthenticationDataState = createSelector(getDataState,
  (dataState: IDataState): IAuthenticationDataState => dataState.authentication
);

export const getAuthenticationIsAuthenticated = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getIsAuthenticated);
export const getAuthenticationIsNewRegisterUser = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getIsNewRegisterUser);
export const getAuthenticationJwtToken = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getJwtToken);
export const getAuthenticationStatus = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getStatus);
export const getAuthenticationAccount = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getAccount);
export const getAuthenticationStatusForRegisterdUser =
            createSelector(getAuthenticationDataState, fromAuthenticationReducer.getStatusIfUserStatusNotChanged);
export const getAuthenticationSubPortalName = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getSubPortalName);
export const getAuthenticationIsMFAEnabled = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getIsMFAEnabled);
export const getAuthenticationIsMFAAuthenticated = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getIsMFAAuthenticated);
export const getAuthenticationMFAAuthSetup = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getMFAAuthSetup);
export const getAuthenticationMFAAuthCodeType = createSelector(getAuthenticationDataState, fromAuthenticationReducer.getMFAAuthCodeType);

// Data: Stage State selectors
export const getStageDataState = createSelector(getDataState,
  (dataState: IDataState): IStageDataState => dataState.stage
);

export const getStageDataCurrentStageId = createSelector(getStageDataState, fromStageReducer.getCurrentStageId);
export const getStageDataCurrentStageName = createSelector(getStageDataState, fromStageReducer.getCurrentStageName);
export const getStageDataHasStageInfo = createSelector(getStageDataState, fromStageReducer.hasStageInfo);

export const getStageDataGetStageMobileInfo = createSelector(getStageDataState, fromStageReducer.getStageMobileInfo);
export const getStageDataGetStagePages = createSelector(getStageDataState, fromStageReducer.getStagePages);
export const getStageDataGetStagePagesLength = createSelector(getStageDataState, fromStageReducer.getStagePagesLength);

export const getStageDataHasStagePages = createSelector(getStageDataState, fromStageReducer.hasStagePages);
export const getStageDataGetSelectedPageId = createSelector(getStageDataState, fromStageReducer.getSelectedPageId);
export const getStageDataGetSelectedPageData = createSelector(getStageDataState, fromStageReducer.getSelectedPageData);

export const getStageDataGetAttachmentEntities = createSelector(getStageDataState, fromStageReducer.getAttachmentEntities);
export const getStageDataGetReadAttachments = createSelector(getStageDataState, fromStageReducer.getReadAttachments);

export const getStageDataGetAttachmentDownloadings = createSelector(getStageDataState, fromStageReducer.getAttachmentDownloadings);
export const getStageDataGetAttachmentDownloadingStatuses = createSelector(getStageDataState, fromStageReducer.getAttachmentDownloadingStatuses);
export const getStageDataIsDataLoading = createSelector(getStageDataState, fromStageReducer.isDataLoading);

export const getStageDataAllAttachmentsDownloaded = createSelector(getStageDataState, fromStageReducer.allAttachmentsDownloaded);
export const getStageDataAllAttachmentsConfirmed = createSelector(getStageDataState, fromStageReducer.allAttachmentsConfirmed);




// WEBPACK FOOTER //
// ./src/app/stateManagement/rootReducer.ts